// @ts-nocheck
/**
 * interactive-image-hotspots.js
 * Controlador vanilla para seções .interactive-image-hotspots
 * - Inicializa cada seção dinamicamente
 * - Gerencia resize (debounce), troca coordenadas desktop/mobile
 * - Interação: hover (desktop), click/touch (mobile), foco teclado, Esc, clique fora
 * - Acessibilidade: aria-expanded, focus management
 * - Apenas um popover aberto por vez por seção
 * - Suporte a eventos Shopify: shopify:section:load, shopify:section:unload, shopify:block:select, shopify:block:deselect
 */
(function () {
    "use strict";

    const SECTION_SELECTOR = ".interactive-image-hotspots[data-section-id]";
    const MOBILE_MAX = 749; // para media queries

    // Utilitário debounce simples (sem tipagem TS)
    function debounce(fn, delay) {
        var t;
        return function () {
            clearTimeout(t);
            var args = arguments;
            var ctx = this;
            t = setTimeout(function () {
                fn.apply(ctx, args);
            }, delay);
        };
    }

    function isMobileViewport() {
        return window.innerWidth <= MOBILE_MAX;
    }

    class InteractiveImageHotspotsSection {
        constructor(root) {
            this.root = root;
            this.id = root.getAttribute("data-section-id");
            /** @type {HTMLElement|null} */
            this.mediaEl = root.querySelector(
                ".interactive-image-hotspots__media"
            );
            // Coleção dos wrappers de hotspots na seção
            this.hotspotWrappers = Array.from(
                root.querySelectorAll("[data-hotspot-wrapper]")
            );
            this.openOnHoverDesktop =
                this.root.getAttribute("data-open-on-hover-desktop") === "true";
            this.openOnClickMobile =
                this.root.getAttribute("data-open-on-click-mobile") === "true";
            this.closeOnOutsideClick =
                this.root.getAttribute("data-close-on-outside-click") ===
                "true";
            this.closeOnEscape =
                this.root.getAttribute("data-close-on-escape") === "true";
            this.respectReduced =
                this.root.getAttribute("data-respect-reduced-motion") ===
                "true";
            this.visibilityMode =
                this.root.getAttribute("data-visibility-mode") || "both";
            this.isDestroyed = false;
            this.currentlyOpen = null; // Wrapper aberto
            this.resizeHandler = debounce(this.handleResize.bind(this), 150);
            this.boundOutside = this.handleDocumentPointer.bind(this);
            this.boundKeydown = this.handleDocumentKeydown.bind(this);
            /** @type {HTMLElement|null} */
            this.lastFocusButton = null;
            // Placeholder para possível uso futuro (consistência com destroy)
            this.mutationObserver = null;
            this.modeClassSync();
            this.init();
        }

        init() {
            this.attachEvents();
            this.applyReducedMotionClass();
            this.positionAll();
            this.ensureVisibilityByMode();
        }

        destroy() {
            this.isDestroyed = true;
            window.removeEventListener("resize", this.resizeHandler);
            document.removeEventListener("pointerdown", this.boundOutside);
            document.removeEventListener("keydown", this.boundKeydown);
            this.hotspotWrappers.forEach((w) => this.teardownHotspot(w));
            this.hotspotWrappers = [];
            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
            }
        }

        attachEvents() {
            window.addEventListener("resize", this.resizeHandler, {
                passive: true,
            });
            if (this.closeOnOutsideClick) {
                document.addEventListener("pointerdown", this.boundOutside);
            }
            if (this.closeOnEscape) {
                document.addEventListener("keydown", this.boundKeydown);
            }
            this.hotspotWrappers.forEach((w) => this.initHotspot(w));
        }

        modeClassSync() {
            this.root.classList.toggle("is-mobile", isMobileViewport());
            this.root.classList.toggle("is-desktop", !isMobileViewport());
        }

        applyReducedMotionClass() {
            if (!this.respectReduced) return;
            const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
            if (mq.matches)
                this.root.classList.add(
                    "interactive-image-hotspots--reduced-motion"
                );
        }

        handleResize() {
            if (this.isDestroyed) return;
            this.modeClassSync();
            this.positionAll();
        }

        ensureVisibilityByMode() {
            // Ajustar aria-hidden conforme modo de visibilidade e viewport
            const viewportIsMobile = isMobileViewport();
            let hidden = false;
            if (this.visibilityMode === "desktop_only" && viewportIsMobile)
                hidden = true;
            if (this.visibilityMode === "mobile_only" && !viewportIsMobile)
                hidden = true;
            this.root.setAttribute("aria-hidden", hidden ? "true" : "false");
        }

        positionAll() {
            const mobile = isMobileViewport();
            this.ensureVisibilityByMode();
            // Debounce visual: frame
            window.requestAnimationFrame(() => {
                this.hotspotWrappers.forEach((wrapper) => {
                    this.updateWrapperPosition(wrapper, mobile);
                });
            });
        }

        /**
         * Atualiza posição, visibilidade e lado do popover para um wrapper.
         * @param {HTMLElement} wrapper
         * @param {boolean} mobile
         */
        updateWrapperPosition(wrapper, mobile) {
            const btn = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-hotspot-button]")
            );
            const desktopX = parseFloat(
                wrapper.getAttribute("data-desktop-x") || "50"
            );
            const desktopY = parseFloat(
                wrapper.getAttribute("data-desktop-y") || "50"
            );
            const mobileX = parseFloat(
                wrapper.getAttribute("data-mobile-x") || String(desktopX)
            );
            const mobileY = parseFloat(
                wrapper.getAttribute("data-mobile-y") || String(desktopY)
            );
            const visibleDesktop =
                wrapper.getAttribute("data-visible-desktop") === "true";
            const visibleMobile =
                wrapper.getAttribute("data-visible-mobile") === "true";

            const x = mobile ? mobileX : desktopX;
            const y = mobile ? mobileY : desktopY;
            // Validação 0-100
            const safeX = isFinite(x) ? Math.min(100, Math.max(0, x)) : 50;
            const safeY = isFinite(y) ? Math.min(100, Math.max(0, y)) : 50;
            // Aplicar variáveis no wrapper (CSS usa left/top)
            wrapper.style.setProperty("--x", String(safeX));
            wrapper.style.setProperty("--y", String(safeY));

            // Visibilidade por wrapper
            let hide = false;
            if (mobile && !visibleMobile) hide = true;
            if (!mobile && !visibleDesktop) hide = true;
            wrapper.classList.toggle("is-hidden", hide);

            // Atualizar lado do popover
            const popover = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-popover]")
            );
            if (popover) {
                popover.classList.remove(
                    "interactive-image-hotspots__popover--top",
                    "interactive-image-hotspots__popover--right",
                    "interactive-image-hotspots__popover--bottom",
                    "interactive-image-hotspots__popover--left"
                );
                const sideDesktop =
                    wrapper.getAttribute("data-side-desktop") || "auto";
                const sideMobile =
                    wrapper.getAttribute("data-side-mobile") || "auto";
                const side = mobile ? sideMobile : sideDesktop;
                const resolvedSide = this.resolveSide(side, safeX, safeY);
                popover.classList.add(
                    "interactive-image-hotspots__popover--" + resolvedSide
                );
            }
        }

        /**
         * Resolve lado final do popover.
         * @param {string} side Valor configurado (auto|top|right|bottom|left)
         * @param {number} xPerc
         * @param {number} yPerc
         * @returns {"top"|"right"|"bottom"|"left"}
         */
        resolveSide(side, xPerc, yPerc) {
            if (["top", "right", "bottom", "left"].includes(side)) return side;
            // Heurística simples: se está na metade inferior -> top; se perto topo -> bottom; senão left/right conforme x
            if (yPerc > 60) return "top";
            if (yPerc < 40) return "bottom";
            if (xPerc > 50) return "left";
            return "right";
        }

        /**
         * Inicializa listeners de um hotspot.
         * @param {HTMLElement} wrapper
         */
        initHotspot(wrapper) {
            const button = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-hotspot-button]")
            );
            const popover = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-popover]")
            );
            if (!button || !popover) return;

            // Eventos
            button.addEventListener("click", (e) => {
                if (isMobileViewport()) {
                    if (this.openOnClickMobile) {
                        e.preventDefault();
                        this.toggle(wrapper, { source: "pointer" });
                    }
                } else {
                    // Desktop: clique também pode togglar
                    e.preventDefault();
                    this.toggle(wrapper, { source: "pointer" });
                }
            });

            if (this.openOnHoverDesktop) {
                button.addEventListener("mouseenter", () => {
                    if (!isMobileViewport())
                        this.open(wrapper, { source: "hover" });
                });
                wrapper.addEventListener("mouseleave", (ev) => {
                    if (!isMobileViewport()) {
                        // Fechar somente se ainda aberto e foco não está dentro
                        if (this.currentlyOpen === wrapper) {
                            const related = ev.relatedTarget;
                            if (
                                !(related instanceof Node) ||
                                !wrapper.contains(related)
                            )
                                this.close(wrapper, { restoreFocus: false });
                        }
                    }
                });
            }

            button.addEventListener("keydown", (e) => {
                const kev = /** @type {KeyboardEvent} */ (e);
                if (kev.key === "Enter" || kev.key === " ") {
                    e.preventDefault();
                    this.toggle(wrapper, { source: "keyboard" });
                }
            });
        }

        /**
         * Limpeza de hotspot (placeholder caso precise no futuro).
         * @param {HTMLElement} wrapper
         */
        teardownHotspot(wrapper) {
            // Sem necessidade de detalhar (limpeza simples) - rely on GC
        }

        /**
         * Abre ou fecha wrapper.
         * @param {HTMLElement} wrapper
         * @param {{source?: string}} meta
         */
        toggle(wrapper, meta) {
            if (this.currentlyOpen === wrapper) {
                this.close(wrapper, {
                    restoreFocus: meta && meta.source === "keyboard",
                });
            } else {
                this.open(wrapper, meta || {});
            }
        }

        /**
         * Abre popover.
         * @param {HTMLElement} wrapper
         * @param {{source?: string}} meta
         */
        open(wrapper, meta = {}) {
            if (this.currentlyOpen && this.currentlyOpen !== wrapper) {
                this.close(this.currentlyOpen, { restoreFocus: false });
            }
            const button = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-hotspot-button]")
            );
            const popover = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-popover]")
            );
            if (!button || !popover || wrapper.classList.contains("is-hidden"))
                return;
            wrapper.classList.add("is-active");
            button.classList.add("is-active");
            popover.classList.add("is-active");
            popover.setAttribute("aria-hidden", "false");
            button.setAttribute("aria-expanded", "true");
            this.currentlyOpen = wrapper;
            if (meta.source === "keyboard") {
                this.lastFocusButton = button;
                // Mover foco para popover
                setTimeout(() => {
                    if (popover && typeof popover.focus === "function") {
                        try {
                            popover.focus({ preventScroll: true });
                        } catch (_) {}
                    }
                }, 10);
            }
        }

        /**
         * Fecha popover.
         * @param {HTMLElement} wrapper
         * @param {{restoreFocus?: boolean}} [opts]
         */
        close(wrapper, { restoreFocus = false } = {}) {
            const button = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-hotspot-button]")
            );
            const popover = /** @type {HTMLElement|null} */ (
                wrapper.querySelector("[data-popover]")
            );
            if (!button || !popover) return;
            wrapper.classList.remove("is-active");
            button.classList.remove("is-active");
            popover.classList.remove("is-active");
            popover.setAttribute("aria-hidden", "true");
            button.setAttribute("aria-expanded", "false");
            if (this.currentlyOpen === wrapper) this.currentlyOpen = null;
            if (
                restoreFocus &&
                this.lastFocusButton &&
                typeof this.lastFocusButton.focus === "function"
            ) {
                try {
                    this.lastFocusButton.focus({ preventScroll: true });
                } catch (_) {}
            }
        }

        /** Fecha qualquer popover aberto. */
        closeAll() {
            if (this.currentlyOpen)
                this.close(this.currentlyOpen, { restoreFocus: false });
        }

        /**
         * Handler clique fora.
         * @param {PointerEvent} e
         */
        handleDocumentPointer(e) {
            if (!this.currentlyOpen) return;
            const target = e.target;
            const rootContains = this.root.contains(target);
            // Clique totalmente fora da seção
            if (!rootContains) {
                this.closeAll();
                return;
            }
            // Se estamos em viewport mobile, queremos fechar ao tocar em qualquer área livre dentro da seção
            if (isMobileViewport()) {
                const activeWrapper = this.currentlyOpen;
                if (activeWrapper) {
                    const btn = activeWrapper.querySelector(
                        "[data-hotspot-button]"
                    );
                    const pop = activeWrapper.querySelector("[data-popover]");
                    const insideButton = btn && btn.contains(target);
                    const insidePopover = pop && pop.contains(target);
                    if (!insideButton && !insidePopover) {
                        this.close(activeWrapper, { restoreFocus: false });
                    }
                }
            }
        }

        /**
         * Handler tecla Esc.
         * @param {KeyboardEvent} e
         */
        handleDocumentKeydown(e) {
            if (e.key === "Escape" || e.key === "Esc") {
                if (this.currentlyOpen) {
                    this.close(this.currentlyOpen, { restoreFocus: true });
                }
            }
        }

        // Editor de tema: destacar block selecionado
        /**
         * Seleciona (abre) bloco no editor.
         * @param {string} blockId
         */
        selectBlock(blockId) {
            const target = this.hotspotWrappers.find(
                (w) => w.getAttribute("data-block-id") === blockId
            );
            if (target) {
                this.open(target, { source: "keyboard" });
                // Scroll into view leve
                setTimeout(() => {
                    try {
                        target.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                            inline: "center",
                        });
                    } catch (_) {}
                }, 50);
            }
        }

        /**
         * Deseleciona bloco no editor.
         * @param {string} blockId
         */
        deselectBlock(blockId) {
            const target = this.hotspotWrappers.find(
                (w) => w.getAttribute("data-block-id") === blockId
            );
            if (target && this.currentlyOpen === target) {
                this.close(target, { restoreFocus: false });
            }
        }
    }

    const instances = new Map();

    function initAll() {
        document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
            const id = section.getAttribute("data-section-id");
            if (!instances.has(id)) {
                instances.set(id, new InteractiveImageHotspotsSection(section));
            }
        });
    }

    function destroySection(sectionId) {
        const instance = instances.get(sectionId);
        if (instance) {
            instance.destroy();
            instances.delete(sectionId);
        }
    }

    // Inicialização DOM pronto
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initAll);
    } else {
        initAll();
    }

    // Eventos Shopify para edição dinâmica
    document.addEventListener("shopify:section:load", (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const section = target.matches(SECTION_SELECTOR)
            ? target
            : target.querySelector(SECTION_SELECTOR);
        if (section instanceof HTMLElement) {
            const id = section.getAttribute("data-section-id");
            if (!id) return;
            destroySection(id);
            instances.set(id, new InteractiveImageHotspotsSection(section));
        }
    });

    document.addEventListener("shopify:section:unload", (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const section = target.matches(SECTION_SELECTOR)
            ? target
            : target.querySelector(SECTION_SELECTOR);
        if (section instanceof HTMLElement) {
            const id = section.getAttribute("data-section-id");
            if (!id) return;
            destroySection(id);
        }
    });

    /**
     * Evento disparado pelo editor ao selecionar um bloco.
     * Shopify envia um CustomEvent com shape { detail: { blockId: string } }
     * Fazemos cast para CustomEvent para satisfazer TS/linters quando presentes.
     * @param {CustomEvent & { detail?: { blockId?: string } }} e
     */
    document.addEventListener("shopify:block:select", (e) => {
        const evt =
            /** @type {CustomEvent & { detail?: { blockId?: string } }} */ (e);
        const blockId = evt.detail && evt.detail.blockId;
        // Encontrar wrapper
        if (!blockId) return;
        document.querySelectorAll(SECTION_SELECTOR).forEach((sec) => {
            const id = sec.getAttribute("data-section-id");
            const inst = instances.get(id);
            if (inst) inst.selectBlock(blockId);
        });
    });

    /**
     * Evento disparado pelo editor ao desselecionar um bloco.
     * @param {CustomEvent & { detail?: { blockId?: string } }} e
     */
    document.addEventListener("shopify:block:deselect", (e) => {
        const evt =
            /** @type {CustomEvent & { detail?: { blockId?: string } }} */ (e);
        const blockId = evt.detail && evt.detail.blockId;
        if (!blockId) return;
        document.querySelectorAll(SECTION_SELECTOR).forEach((sec) => {
            const id = sec.getAttribute("data-section-id");
            const inst = instances.get(id);
            if (inst) inst.deselectBlock(blockId);
        });
    });
})();
